<script lang="ts" setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardSummary from '../components/WizardSummary.vue';
import { validationRFC } from '../../../../helpers';
import {
  findClientByTributaryCode,
  createAudits,
  findClient,
} from '../../../../repositories/landing.repository';
import storePolicy from '@store/policy';

const emit = defineEmits<{
  (e: 'next', payload: { updatesClientInputs: any[] }): void;
  (e: 'back'): void;
}>();

const { t } = useI18n();
const store = storePolicy();

const rfc = ref<string>('');
const email = ref<string>(store.data?.email ?? '');
const phone = ref<string>(store.data?.phone ?? '');
const error = ref<string>('');
const loading = ref<boolean>(false);

const validEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

// El RFC se comprueba recién al dar "Continuar": valida formato y,
// si el cliente existe, precarga sus datos para el paso "Revisa tus datos"
const handleContinue = async () => {
  error.value = '';

  const rfcValue = rfc.value.trim().toUpperCase();
  if (!validationRFC(rfcValue)) {
    error.value = t('landing.wizard.tus_datos.error_rfc');
    return;
  }
  if (!validEmail(email.value)) {
    error.value = t('landing.wizard.tus_datos.error_email');
    return;
  }
  if (!phone.value || phone.value.length < 8) {
    error.value = t('landing.wizard.tus_datos.error_telefono');
    return;
  }

  try {
    loading.value = true;
    const { locations, client } = await findClientByTributaryCode(rfcValue);

    let updatesClientInputs: any[] = [];

    if (client) {
      store.setProp({ key: 'client', value: client });
    } else {
      store.setProp({ key: 'client', value: { rfc: rfcValue } });
    }

    if (locations && client) {
      updatesClientInputs = [
        { key: 'tradeName', value: client.tradeName },
        { key: 'phone1', value: client.phone1 },
        {
          key: 'companyGPSId',
          value: {
            id: client.companyGPS.id,
            nombre: client.companyGPS.description,
          },
        },
        { key: 'address', value: client.address },
        { key: 'postCode', value: client.postCode },
        {
          key: 'colony',
          options: locations,
          value: {
            id: client.neighborhood.id,
            nombre: client.neighborhood.description,
          },
        },
        { key: 'city', value: client.city.description },
        { key: 'state', value: client.state.description },
      ];
    }

    store.setProp({
      key: 'data',
      value: { ...store.data, email: email.value, phone: phone.value },
    });

    // Se ejecuta acá (no en "Cotiza") porque recién ahora tenemos email y
    // teléfono; el backend de /audits los requiere.
    createAudits({
      email: email.value,
      phone: phone.value,
      asset: store.data?.asset?.nombre ?? null,
    });
    findClient(email.value);

    emit('next', { updatesClientInputs });
  } catch (e: any) {
    error.value = e?.message ?? t('global.ocurrio_un_error');
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <section class="wz-screen">
    <div class="wz-grid wz-grid--datos">
      <WizardSummary />

      <aside class="wz-card">
        <h2>{{ t('landing.wizard.tus_datos.titulo_card') }}</h2>
        <p class="sub">{{ t('landing.wizard.tus_datos.subtitulo_card') }}</p>

        <div class="wz-field">
          <label for="wz-rfc">{{
            t('landing.wizard.tus_datos.rfc_label')
          }}</label>
          <input
            id="wz-rfc"
            v-model="rfc"
            type="text"
            maxlength="13"
            placeholder="ABCD123456EF7"
            style="text-transform: uppercase"
          />
          <div class="wz-help">
            {{ t('landing.wizard.tus_datos.rfc_help') }}
          </div>
        </div>

        <div class="wz-field">
          <label for="wz-email">{{
            t('landing.wizard.tus_datos.correo')
          }}</label>
          <input
            id="wz-email"
            v-model="email"
            type="email"
            placeholder="tucorreo@empresa.com"
          />
          <div class="wz-help">
            {{ t('landing.wizard.tus_datos.correo_help') }}
          </div>
        </div>

        <div class="wz-field">
          <label for="wz-phone">{{
            t('landing.wizard.tus_datos.telefono')
          }}</label>
          <input
            id="wz-phone"
            v-model="phone"
            type="tel"
            placeholder="55 1234 5678"
          />
          <div class="wz-help">
            {{ t('landing.wizard.tus_datos.telefono_help') }}
          </div>
        </div>

        <p v-if="error" class="wz-error">{{ error }}</p>

        <button
          type="button"
          class="wz-cta blue"
          :disabled="loading"
          @click="handleContinue"
        >
          {{
            loading
              ? t('landing.wizard.tus_datos.comprobando')
              : t('landing.wizard.tus_datos.continuar')
          }}
        </button>
        <button type="button" class="wz-back" @click="emit('back')">
          {{ t('landing.wizard.tus_datos.volver') }}
        </button>
      </aside>
    </div>
  </section>
</template>
